import { WMSCapabilities } from 'ol/format';
import {
  getProxyUrl,
  isExternalUrl,
} from '@ais-ui/react-openlayers';

type AuthorityURL = {
  OnlineResource: string;
  name: string;
};

type BoundingBox = {
  crs: string;
  extent: number[];
  res: (number | null)[];
};

type MetadataURL = {
  Format: string;
  OnlineResource: string;
  type: string;
};

type LegendURL = {
  Format: string;
  OnlineResource: string;
  size: number[];
};

type Style = {
  LegendURL: LegendURL[];
  Name: string;
  Title: string;
};

export type LayerInfo = {
  Abstract: string;
  AuthorityURL: AuthorityURL[];
  BoundingBox: BoundingBox[];
  CRS: string[];
  EX_GeographicBoundingBox: number[];
  MetadataURL: MetadataURL[];
  Name: string;
  Style: Style[];
  Title: string;
  noSubsets: boolean;
  opaque: boolean;
  queryable: boolean;
};

export const getCapabilities = async (
  url: string
): Promise<{ layers: LayerInfo[]; version: string; useProxy: boolean }> => {
  const urlObj = new URL(url);

  // Get the existing query parameters
  const params = urlObj.searchParams;

  // Strip WMS-controlled params (case-insensitive) to avoid duplicates
  const paramsToStrip = ['service', 'request'];
  for (const param of [...params.keys()]) {
    if (paramsToStrip.includes(param.toLowerCase())) {
      params.delete(param);
    }
  }

  // Add WMS parameters
  params.set('SERVICE', 'WMS');
  params.set('REQUEST', 'GetCapabilities');

  // Update the search query for the URL object
  urlObj.search = params.toString();

  const fullUrl = urlObj.toString();
  const external = isExternalUrl(fullUrl);

  const parseCapabilities = (text: string): { layers: LayerInfo[]; version: string } => {
    const parser = new WMSCapabilities();
    const capabilities = parser.read(text);
    if (capabilities?.Capability?.Layer?.Layer) {
      const layers = capabilities.Capability.Layer.Layer;
      const version = capabilities.version || '';
      return { layers, version };
    }
    throw new Error('Failed to parse capabilities - invalid response structure');
  };

  const fetchAndParse = async (fetchUrl: string): Promise<{ layers: LayerInfo[]; version: string }> => {
    const response = await fetch(fetchUrl);
    if (!response.ok) {
      throw new Error(`HTTP error: ${response.status} ${response.statusText}`);
    }
    const text = await response.text();
    return parseCapabilities(text);
  };

  // Try direct fetch first for external URLs
  if (external) {
    try {
      // Try direct fetch - CORS errors occur at the fetch level
      const response = await fetch(fullUrl);
      if (response.ok) {
        const text = await response.text();
        const result = parseCapabilities(text);
        return { ...result, useProxy: false };
      }
      // Non-OK response - don't use proxy for server errors (4xx, 5xx)
      throw new Error(`HTTP error: ${response.status} ${response.statusText}`);
    } catch (error) {
      // CORS errors manifest as TypeError with "Failed to fetch" message
      // or network errors that prevent the request from completing
      const isCorsLikeError = error instanceof TypeError || 
        (error instanceof Error && error.message.includes('Failed to fetch'));
      
      if (isCorsLikeError) {
        // Fall back to proxy for CORS errors.
        // Do NOT add to corsProxyRequiredOrigins cache - the useProxy: true return value
        // will be saved to settings and used for this specific WMS service.
        const proxyUrl = getProxyUrl(fullUrl);
        const result = await fetchAndParse(proxyUrl);
        return { ...result, useProxy: true };
      }
      
      // Re-throw non-CORS errors (HTTP errors, parsing errors)
      throw error;
    }
  }

  // Internal URL - fetch directly
  const result = await fetchAndParse(fullUrl);
  return { ...result, useProxy: false };
};
